import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import db from '../services/database';
import { keyManager } from '../services/encryption';
import Icon from '../components/UI/Icon';
import './SettingsPage.css';

function SettingsPage() {
  const { theme, setTheme, fontSize, setFontSize, fontFamily, setFontFamily, lineSpacing, setLineSpacing, themes } = useTheme();
  const [settings, setSettings] = useState({
    offlineMode: true,
    notifications: false,
    autoBackup: true,
    dataRetention: '1year'
  });
  // Removed cloud sync password state
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [storageUsage, setStorageUsage] = useState(null);

  // Load settings
  useEffect(() => {
    async function loadSettings() {
      try {
        const savedSettings = await db.settings.get('app');
        if (savedSettings) {
          setSettings(prev => ({ ...prev, ...savedSettings.value }));
        }

        // Calculate storage usage
        if (navigator.storage && navigator.storage.estimate) {
          const estimate = await navigator.storage.estimate();
          setStorageUsage({
            used: estimate.usage,
            quota: estimate.quota
          });
        }
      } catch (err) {
        console.error('Failed to load settings:', err);
      }
    }
    loadSettings();
  }, []);

  // Save settings
  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await db.settings.put({
        key: 'app',
        value: settings,
        updatedAt: new Date().toISOString()
      });

      // If sync is enabled, initialize key manager
      if (settings.syncEnabled && settings.syncPassword) {
        await keyManager.initialize(settings.syncPassword);
      }

      setSaveMessage('Settings saved successfully');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      console.error('Failed to save settings:', err);
      setSaveMessage('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  // Format bytes
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Clear cache
  const handleClearCache = async () => {
    if (!window.confirm('This will clear cached Bible data. Continue?')) return;
    
    try {
      await db.chapters.clear();
      await db.verses.clear();
      setSaveMessage('Cache cleared successfully');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      console.error('Failed to clear cache:', err);
    }
  };

  // Export data
  const handleExportData = async () => {
    try {
      const data = {
        journals: await db.journals.toArray(),
        bookmarks: await db.bookmarks.toArray(),
        highlights: await db.highlights.toArray(),
        notes: await db.notes.toArray(),
        themes: await db.themes.toArray(),
        readingPlans: await db.readingPlans.toArray(),
        exportedAt: new Date().toISOString()
      };

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `truthbound-backup-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);

      setSaveMessage('Data exported successfully');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      console.error('Failed to export data:', err);
    }
  };

  // Import data
  const handleImportData = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = JSON.parse(event.target.result);

        // Import each collection
        if (data.journals) await db.journals.bulkPut(data.journals);
        if (data.bookmarks) await db.bookmarks.bulkPut(data.bookmarks);
        if (data.highlights) await db.highlights.bulkPut(data.highlights);
        if (data.notes) await db.notes.bulkPut(data.notes);
        if (data.themes) await db.themes.bulkPut(data.themes);
        if (data.readingPlans) await db.readingPlans.bulkPut(data.readingPlans);

        setSaveMessage('Data imported successfully');
        setTimeout(() => setSaveMessage(''), 3000);
      } catch (err) {
        console.error('Failed to import data:', err);
        setSaveMessage('Failed to import data. Invalid file format.');
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="settings-page page">
      <header className="page-header">
        <h1 className="page-title">Settings</h1>
        {saveMessage && (
          <div className={`save-message ${saveMessage.includes('Failed') ? 'error' : 'success'}`}>
            {saveMessage}
          </div>
        )}
      </header>

      <div className="settings-content">
        {/* Appearance */}
        <section className="settings-section">
          <h2 className="section-title">
            <Icon name="moon" size={20} />
            Appearance
          </h2>

          <div className="setting-item">
            <label className="setting-label">Theme</label>
            <div className="theme-options">
              {Object.entries(themes).map(([key, themeConfig]) => (
                <button
                  key={key}
                  className={`theme-option ${theme === key ? 'active' : ''}`}
                  onClick={() => setTheme(key)}
                  style={{ 
                    background: themeConfig.colors.background,
                    color: themeConfig.colors.textPrimary,
                    borderColor: themeConfig.colors.border
                  }}
                  title={themeConfig.description}
                >
                  <span className="theme-preview" style={{
                    background: `linear-gradient(135deg, ${themeConfig.colors.surface} 0%, ${themeConfig.colors.accent} 100%)`
                  }}></span>
                  <span className="theme-name">{themeConfig.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="setting-item">
            <label className="setting-label">Font Size</label>
            <div className="font-size-options">
              {['small', 'medium', 'large', 'xlarge'].map(size => (
                <button
                  key={size}
                  className={`size-option ${fontSize === size ? 'active' : ''}`}
                  onClick={() => setFontSize(size)}
                >
                  {size === 'small' && 'A'}
                  {size === 'medium' && 'A'}
                  {size === 'large' && 'A'}
                  {size === 'xlarge' && 'A'}
                </button>
              ))}
            </div>
          </div>

          <div className="setting-item">
            <label className="setting-label">Font Family</label>
            <select
              className="setting-select"
              value={fontFamily}
              onChange={(e) => setFontFamily(e.target.value)}
            >
              <option value="merriweather">Merriweather (Serif)</option>
              <option value="georgia">Georgia (Classic)</option>
              <option value="sourceSans">Source Sans (Modern)</option>
              <option value="system">System Default</option>
            </select>
          </div>

          <div className="setting-item">
            <label className="setting-label">Line Spacing</label>
            <select
              className="setting-select"
              value={lineSpacing}
              onChange={(e) => setLineSpacing(e.target.value)}
            >
              <option value="compact">Compact</option>
              <option value="comfortable">Comfortable</option>
              <option value="relaxed">Relaxed</option>
            </select>
          </div>
        </section>


        {/* Local Backup Only (if desired, keep this section) */}
        <section className="settings-section">
          <h2 className="section-title">
            <Icon name="database" size={20} />
            Backup
          </h2>

          <div className="setting-item toggle-item">
            <div className="setting-info">
              <label className="setting-label">Auto Backup</label>
              <p className="setting-description">
                Automatically backup your data locally
              </p>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                checked={settings.autoBackup}
                onChange={(e) => setSettings(prev => ({ ...prev, autoBackup: e.target.checked }))}
              />
              <span className="toggle-slider" />
            </label>
          </div>

          <div className="setting-actions">
            <button className="btn btn-secondary" onClick={handleExportData}>
              <Icon name="download" size={18} />
              Export Data
            </button>
            <label className="btn btn-secondary">
              <Icon name="upload" size={18} />
              Import Data
              <input
                type="file"
                accept=".json"
                onChange={handleImportData}
                style={{ display: 'none' }}
              />
            </label>
          </div>
        </section>

        {/* Storage */}
        <section className="settings-section">
          <h2 className="section-title">
            <Icon name="download" size={20} />
            Storage
          </h2>

          {storageUsage && (
            <div className="storage-info">
              <div className="storage-bar">
                <div 
                  className="storage-fill"
                  style={{ width: `${(storageUsage.used / storageUsage.quota) * 100}%` }}
                />
              </div>
              <p className="storage-text">
                {formatBytes(storageUsage.used)} used of {formatBytes(storageUsage.quota)}
              </p>
            </div>
          )}

          <div className="setting-item toggle-item">
            <div className="setting-info">
              <label className="setting-label">Offline Mode</label>
              <p className="setting-description">
                Cache Bible data for offline reading
              </p>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                checked={settings.offlineMode}
                onChange={(e) => setSettings(prev => ({ ...prev, offlineMode: e.target.checked }))}
              />
              <span className="toggle-slider" />
            </label>
          </div>

          <div className="setting-actions">
            <button className="btn btn-ghost" onClick={handleClearCache}>
              <Icon name="trash" size={18} />
              Clear Cache
            </button>
          </div>
        </section>

        {/* About */}
        <section className="settings-section">
          <h2 className="section-title">
            <Icon name="info" size={20} />
            About
          </h2>

          <div className="about-info">
            <h3>TruthBound</h3>
            <p>Version 1.8.0</p>
            <p className="about-description">
              A thoughtful Bible companion for deep Scripture engagement. 
              Local-first, privacy-focused, designed for reflection.
            </p>
            <p className="about-links">
              <Link to="/privacy">Privacy Policy</Link>
            </p>
          </div>
        </section>

        <div className="save-section">
          <button 
            className="btn btn-primary btn-lg"
            onClick={handleSaveSettings}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
